import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import _ from "lodash";

import DateTime from "../../../components/DateTime";
import StatusChip from "../../../components/StatusChip";
import SoftButton from "../../../components/SoftButton";
import { useTimeZoneStore } from "../../../stores/timeZone";
import { countries } from "../../../constants/countries";
import { useGetTransaction } from "../hooks";
import { formatTransactionAmount, getDetailEntries } from "../utils/transactionDetails";

/** Presents related values as a flat, responsive definition list. */
function DetailSection({ title, entries }) {
  if (!entries.length) return null;
  return (
    <Box component="section" sx={{ py: 2, borderTop: 1, borderColor: "grey.300" }}>
      <Typography
        component="h3"
        sx={{ fontSize: 15, fontWeight: 700, mb: 1.5, color: "text.primary" }}
      >
        {title}
      </Typography>
      <Box
        component="dl"
        sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
      >
        {entries.map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography component="dt" sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
              {label}
            </Typography>
            <Typography
              component="dd"
              sx={{ m: 0, fontSize: 14, color: "text.primary", overflowWrap: "anywhere" }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  entries: PropTypes.array.isRequired,
};

/** Fetches transaction details without leaving the current reporting page. */
function TransactionDetailModal({ transactionId, transaction, onClose }) {
  const { data, isLoading, isError, refetch } = useGetTransaction({ transactionId });
  const [copyStatus, setCopyStatus] = useState("");
  const timeZone = useTimeZoneStore((state) => state.timeZone);
  // The detail endpoint omits status; retain the status supplied by the list row.
  const details = { ...transaction, ...data };
  const country = countries.find(({ code }) => code === details.country)?.label || details.country;
  const errorEntries = getDetailEntries({ Reason: details.errorReason, Code: details.errorCode });

  /** Copies the complete identifier, with feedback when clipboard access is unavailable. */
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(transactionId);
      setCopyStatus("Transaction ID copied");
    } catch {
      setCopyStatus("Could not copy. Select the ID to copy it manually.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="transaction-detail-title"
      PaperProps={{
        sx: {
          width: "calc(100% - 32px)",
          maxWidth: 800,
          m: 2,
          maxHeight: "calc(100dvh - 32px)",
          borderRadius: "20px",
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 2 }}
      >
        <Typography
          id="transaction-detail-title"
          component="h2"
          sx={{ fontSize: 18, fontWeight: 700 }}
        >
          Transaction details
        </Typography>
        <IconButton aria-label="Close transaction details" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: "24px !important", pb: 0 }}>
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                Amount
              </Typography>
              <Typography
                sx={{
                  fontSize: 36,
                  color: "text.primary",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: "-1px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatTransactionAmount(details.amount, details.currency)}
                {details.currency && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: 16,
                      fontWeight: 500,
                      letterSpacing: 0,
                      color: "text.secondary",
                    }}
                  >
                    {details.currency}
                  </Box>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {details.type && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={_.startCase(details.type.toLowerCase())}
                />
              )}
              {details.status && <StatusChip size="small" label={details.status} />}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: "10px",
              bgcolor: "surface.main",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                Transaction ID
              </Typography>
              <Typography
                sx={(theme) => ({
                  fontFamily: theme.typography.monoFontFamily,
                  fontSize: 12,
                  overflowWrap: "anywhere",
                  userSelect: "all",
                })}
              >
                {transactionId}
              </Typography>
            </Box>
            <Tooltip title="Copy transaction ID">
              <IconButton onClick={copyId} aria-label="Copy transaction ID" size="small">
                <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {copyStatus && (
            <Typography role="status" sx={{ mt: 1, fontSize: 12 }}>
              {copyStatus}
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Box role="status" aria-label="Loading transaction details" sx={{ pb: 3 }}>
            <Skeleton width="35%" height={28} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Box>
        ) : isError ? (
          <Box role="alert" sx={{ pb: 3 }}>
            <Typography sx={{ fontSize: 14, mb: 1.5 }}>
              Transaction details could not be loaded.
            </Typography>
            <SoftButton variant="outlined" color="dark" size="small" onClick={() => refetch()}>
              Try again
            </SoftButton>
          </Box>
        ) : (
          <>
            <DetailSection
              title="Payment details"
              entries={getDetailEntries({
                "Merchant reference": details.merchantReference,
                "Payment method": details.paymentOption,
                Merchant: details.merchant,
                Brand: details.brand,
                Country: country,
              })}
            />
            <DetailSection title="Customer" entries={getDetailEntries(details.customer)} />
            <DetailSection title="Card details" entries={getDetailEntries(details.cardData)} />
            {errorEntries.length > 0 && (
              <Box sx={{ color: "error.main" }}>
                <DetailSection title="Payment error" entries={errorEntries} />
              </Box>
            )}
            <DetailSection
              title="Additional information"
              entries={[
                ["Extra 1", details.extra1 || "—"],
                ["Extra 2", details.extra2 || "—"],
                ["Extra 3", details.extra3 || "—"],
              ]}
            />
            <DetailSection
              title="Timeline"
              entries={[
                ["Created", <DateTime key="created" value={details.createdAt} />],
                ["Last updated", <DateTime key="updated" value={details.updatedAt} />],
              ]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "grey.300",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Times shown in {timeZone}
        </Typography>
        <SoftButton variant="outlined" color="dark" size="small" onClick={onClose}>
          Close
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

TransactionDetailModal.propTypes = {
  transactionId: PropTypes.string.isRequired,
  transaction: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default TransactionDetailModal;
